import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ConsolePanel, Button } from '@/ui';
import { Icon } from '@/icons';
import { useCommunities, useRequireAuth } from '@/hooks';
import type { Community } from '@/core/models/community';

const Card = styled(ConsolePanel)`
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-width: 0;
  > svg {
    color: ${({ theme }) => theme.color.accentCyan};
  }
  a {
    font-weight: 600;
    color: ${({ theme }) => theme.color.textPrimary};
    text-decoration: none;
    overflow-wrap: anywhere;
  }
  small {
    color: ${({ theme }) => theme.color.textMuted};
  }
  button {
    margin-top: auto;
  }
`;
export function CommunityJoinButton({ community }: { community: Community }) {
  const { toggleJoin } = useCommunities();
  const requireAuth = useRequireAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function join() {
    if (busy || !requireAuth(`join ${community.name}`)) return;
    setBusy(true);
    setError('');
    try {
      await toggleJoin(community.id);
    } catch {
      setError('Could not update membership. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button
        variant={community.joined ? 'secondary' : 'outline'}
        size="sm"
        disabled={busy}
        aria-label={`${community.joined ? 'Leave' : 'Join'} ${community.name}`}
        onClick={() => void join()}
      >
        {busy ? 'Updating…' : community.joined ? 'Joined' : 'Join'}
      </Button>
      {error && <small role="alert">{error}</small>}
    </>
  );
}
export function DiscoveryCommunityCard({ community }: { community: Community }) {
  return (
    <Card>
      <Icon name="users-round" size={26} />
      <Link to={`/communities/${community.id}`}>{community.name}</Link>
      <small>{community.members}</small>
      <CommunityJoinButton community={community} />
    </Card>
  );
}
